import React, { useEffect, useState } from 'react';
import { Form, FormFeedback, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import Select, { ActionMeta, SingleValue } from 'react-select';
import { createPlacement, updatePlacement } from '../../api/placementApi';
import CreatableSelect from 'react-select/creatable';
import { MultiValue } from 'react-select/dist/declarations/src/types';
import { Placement } from '../../types/placement';
import { useProfile } from '../../hooks/UserHooks';
import { Site } from '../../types/site';
import { createOption, Option } from '../../helpers/selectHelpers';
import selectSizeOptions from '../../constants/selectSizeOptions';
import { getSites } from '../../api/siteApi';

interface AddModalProps {
    placement: Placement|null;
    isOpen: boolean;
    toggle: () => void;
    onAddData: (newData: Placement) => void;
    onUpdateData: (newData: Placement) => void;
}

const AddEditPlacementModal = ({ placement, isOpen, toggle, onAddData, onUpdateData }: AddModalProps) => {
    const { userProfile } = useProfile();
    const [loading, setLoading] = useState<boolean>(false);
    const [sites, setSites] = useState<Site[]|null>(null);
    const [selectSiteValue, setSelectSiteValue] = useState<SingleValue<Option>>();
    const [selectSizesValue, setSelectSizesValue] = useState<MultiValue<Option>>([]);
    const [sizeOptions, setSizeOptions] = useState<any>(selectSizeOptions);

    useEffect(() => {
        getSites().then(setSites);
    }, []);

    useEffect(() => {
        if (placement) {
            if (placement.site) {
                setSelectSiteValue(createOption(placement.site.publisher.name, String(placement.site.publisher.id)));
            }

            if (placement.sizes) {
                const selectedSizes = placement.sizes.map((size: string) => createOption(size));
                setSelectSizesValue(selectedSizes);
            }

            if (placement.type) {
                handleForm.setFieldValue('type', placement.type);
            }
        }
    }, [placement]);

    const toggleAddModal = () => {
        toggle();
    };

    const selectSiteOnChange = (newValue: SingleValue<Option>, actionMeta: ActionMeta<Option>) => {
        setSelectSiteValue(newValue);
        handleForm.setFieldValue('siteId', newValue ? Number(newValue.value) : '');
    };

    const selectPlacementsOnChange = (newValue: MultiValue<Option>, actionMeta: ActionMeta<Option>) => {
        setSelectSizesValue(newValue);
        handleForm.setFieldValue('sizes', newValue ? newValue.map((value: Option) => value.value) : []);
    };

    const handleCreate = (inputValue: string) => {
        inputValue = inputValue.toLowerCase();
        const validateInputValue = inputValue.match(/^\d+x\d+$/) === null;
        if (validateInputValue) {
            toast.error('Size inválido');
            return;
        }
        toast.warning('Essa funcionalidade ainda não está disponível, caso precise adicionar um novo size, entre em contato com o suporte.');
        return;
        const newOption = createOption(inputValue);
        setSizeOptions([...sizeOptions, newOption]);
        handleForm.values.sizes.push(inputValue);
    };

    const handleForm: any = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: {
            siteId: placement?.site.id || '',
            name: placement?.name || '',
            // sizes: placement?.sizes || [],
            mediaKitUrl: placement?.mediaKitUrl || '',
            mediaKitFile: '',
            maxRichMediaSizeInKb: placement?.maxRichMediaSizeInKb || '',
            maxImageSizeInKb: placement?.maxImageSizeInKb || '',
            acceptsAudioVideo: placement?.acceptsAudioVideo || false,
            maxMediaSizeInKb: placement?.maxMediaSizeInKb || '',
            maxMediaDurationInSeconds: placement?.maxMediaDurationInSeconds || '',
            refreshInSeconds: placement?.refreshInSeconds || '',
            type: placement?.type || '',
            notes: placement?.notes || '',
        },
        validationSchema: Yup.object({
            siteId: Yup.number().integer().required('Campo obrigatório'),
            name: Yup.string().required('Campo obrigatório'),
            // sizes: Yup.array().min(1, 'Campo obrigatório'),
            mediaKitUrl: Yup.string().nullable().optional().matches(
                /^[(http(s)?)://(www.)?a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)$/,
                'URL inválida',
            ),
            mediaKitFile: Yup.mixed().nullable().optional(),
            maxRichMediaSizeInKb: Yup.number().integer().required('Campo obrigatório'),
            maxImageSizeInKb: Yup.number().integer().required('Campo obrigatório'),
            acceptsAudioVideo: Yup.boolean(),
            maxMediaSizeInKb: Yup.number().when(['acceptsAudioVideo', 'type'], {
                is: (acceptsAudioVideo: boolean, type: string) => acceptsAudioVideo || type === 'video' || type === 'audio',
                then: (schema) => schema.integer().required('Campo obrigatório'),
                otherwise: (schema) => schema.integer().optional().nullable(),
            }),
            maxMediaDurationInSeconds: Yup.number().when(['acceptsAudioVideo', 'type'], {
                is: (acceptsAudioVideo: boolean, type: string) => acceptsAudioVideo || type === 'video' || type === 'audio',
                then: (schema) => schema.integer().required('Campo obrigatório'),
                otherwise: (schema) => schema.integer().optional().nullable(),
            }),
            refreshInSeconds: Yup.number().integer().optional().nullable(),
            type: Yup.string().required('Campo obrigatório'),
            notes: Yup.string().nullable().optional(),
        }),
        onSubmit: (values: any) => {
            setLoading(true);
            const formData = new FormData();
            for (const [key, value] of Object.entries(values)) {
                if (value === '' || value === 'undefined' || value === null) {
                    continue;
                }

                if (key === 'sizes' && Array.isArray(value)) {
                    value.forEach((size: string) => {
                        formData.append('sizes[]', size);
                    });
                    continue;
                }

                formData.append(key, String(value));
            }

            const file: HTMLInputElement | null = document.querySelector('#mediaKitFile');
            if (file && file.files && file.files[0]) {
                formData.delete('mediaKitFile');
                formData.append('mediaKitFile', file.files[0]);
            }
            if (placement) {
                updatePlacement(placement.id, formData)
                    .then((res: any) => {
                        toggleAddModal();
                        toast.success('Bloco de anúncio atualizado com sucesso!');
                        onUpdateData(res);
                    })
                    .catch(() => {})
                    .finally(() => {
                        setLoading(false);
                    });
            } else {
                createPlacement(formData)
                    .then((res: any) => {
                        toggleAddModal();
                        toast.success('Bloco de anúncio cadastrado com sucesso!');
                        onAddData(res);
                        handleForm.resetForm();
                    })
                    .catch(() => {})
                    .finally(() => {
                        setLoading(false);
                    });
            }
        },
    });

    return (
        <Modal id="showModal" isOpen={isOpen} toggle={toggleAddModal} fade={false} keyboard={true}
               backdrop={false} centered>
            <Form onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleForm.handleSubmit();
                return false;
            }}>
                <ModalHeader toggle={toggleAddModal} className="bg-light p-3">
                    {placement ? 'Editar' : 'Adicionar'} bloco de anúncio
                </ModalHeader>

                <ModalBody>
                    {userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                        <div className="mb-3">
                            <Label
                                htmlFor="siteId"
                                className="form-label"
                            >
                                Publisher
                            </Label>
                            <Select
                                className="basic-single"
                                classNamePrefix="select"
                                isClearable={true}
                                isSearchable={true}
                                name="siteId"
                                options={sites ? sites.map((site) => {
                                    return createOption(site?.site || site?.app || '', String(site.id));
                                }) : []}
                                styles={{
                                    control: (baseStyles, state) => ({
                                        ...baseStyles,
                                        borderColor: !!(handleForm.touched.siteId && handleForm.errors.siteId) ? 'red' : '#ced4da',
                                    }),
                                }}
                                onChange={selectSiteOnChange}
                                value={selectSiteValue}
                                isDisabled={!!placement}
                            />
                            {handleForm.touched.siteId && handleForm.errors.siteId ? (
                                <FormFeedback
                                    type="invalid"
                                    style={{ display: 'block' }}>{handleForm.errors.siteId}</FormFeedback>
                            ) : null}
                        </div>
                    )}
                    <div className="mb-3">
                        <Label
                            htmlFor="name"
                            className="form-label"
                        >
                            Nome do bloco de anúncio
                        </Label>
                        <Input
                            className="form-control"
                            type="text"
                            id="name"
                            name="name"
                            onChange={handleForm.handleChange}
                            onBlur={handleForm.handleBlur}
                            value={handleForm.values.name}
                            invalid={
                                !!(handleForm.touched.name && handleForm.errors.name)
                            }
                        />
                        {handleForm.touched.name && handleForm.errors.name ? (
                            <FormFeedback
                                type="invalid">{handleForm.errors.name}</FormFeedback>
                        ) : null}
                    </div>
                    <div className="mb-3">
                        <Label
                            htmlFor="sizes"
                            className="form-label"
                        >
                            Sizes
                        </Label>
                        <CreatableSelect
                            className="basic-single"
                            classNamePrefix="select"
                            isMulti
                            isClearable={true}
                            isSearchable={true}
                            onCreateOption={handleCreate}
                            name="sizes"
                            styles={{
                                control: (baseStyles) => ({
                                    ...baseStyles,
                                    borderColor: !!(handleForm.touched.sizes && handleForm.errors.sizes) ? 'red' : '#ced4da',
                                }),
                            }}
                            options={sizeOptions}
                            onChange={selectPlacementsOnChange}
                            value={selectSizesValue}
                        />
                        {handleForm.touched.sizes && handleForm.errors.sizes ? (
                            <FormFeedback
                                type="invalid" style={{display: 'block'}}>{handleForm.errors.sizes}</FormFeedback>
                        ) : null}
                    </div>
                    <div className="mb-3">
                        <div className="row">
                            <div className="col">
                                <Label
                                    htmlFor="mediaKitUrl"
                                    className="form-label"
                                >
                                    Mídia Kit - URL
                                </Label>
                                {placement?.mediaKitUrl && (
                                    <a href={placement.mediaKitUrl} target="_blank" rel="noreferrer">
                                        <i className={'ri-external-link-line'}></i>
                                    </a>
                                )}<Input
                                    className="form-control"
                                    type="text"
                                    id="mediaKitUrl"
                                    name="mediaKitUrl"
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    value={handleForm.values.mediaKitUrl}
                                    invalid={
                                        !!(handleForm.touched.mediaKitUrl && handleForm.errors.mediaKitUrl)
                                    }
                                />
                                {handleForm.touched.mediaKitUrl && handleForm.errors.mediaKitUrl ? (
                                    <FormFeedback
                                        type="invalid">{handleForm.errors.mediaKitUrl}</FormFeedback>
                                ) : null}
                            </div>
                            <div className="col">
                                <Label
                                    htmlFor="mediaKitFile"
                                    className="form-label"
                                >
                                    Mídia Kit - Arquivo PDF
                                </Label>
                                {placement?.mediaKitFile && (
                                    <a href={placement.mediaKitFile} target="_blank" rel="noreferrer">
                                        <i className={'ri-external-link-line'}></i>
                                    </a>
                                )}<Input
                                    className="form-control"
                                    type="file"
                                    id="mediaKitFile"
                                    name="mediaKitFile"
                                    accept=".pdf"
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    value={handleForm.values.mediaKitFile}
                                    invalid={
                                        !!(handleForm.touched.mediaKitFile && handleForm.errors.mediaKitFile)
                                    }
                                />
                                {handleForm.touched.mediaKitFile && handleForm.errors.mediaKitFile ? (
                                    <FormFeedback
                                        type="invalid">{handleForm.errors.mediaKitFile}</FormFeedback>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    <div className="mb-3">
                        <div className="row">
                            <div className="col">
                                <Input
                                    name="type"
                                    type="radio"
                                    id="type_display"
                                    value="display"
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    checked={handleForm.values.type === 'display'}
                                    disabled={!!placement}
                                    invalid={
                                        !!(handleForm.touched.type && handleForm.errors.type)
                                    }
                                />
                                {' '}
                                <Label
                                    htmlFor="type_display"
                                    className="form-label"
                                >
                                    Display
                                </Label>
                                {handleForm.touched.type && handleForm.errors.type ? (
                                    <FormFeedback
                                        type="invalid"
                                        style={{display: 'block'}}>{handleForm.errors.type}</FormFeedback>
                                ) : null}
                            </div>
                            <div className="col">
                                <Input
                                    name="type"
                                    type="radio"
                                    id="type_video"
                                    value="video"
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    checked={handleForm.values.type === 'video'}
                                    disabled={!!placement}
                                    invalid={
                                        !!(handleForm.touched.type && handleForm.errors.type)
                                    }
                                />
                                {' '}
                                <Label
                                    htmlFor="type_video"
                                    className="form-label"
                                    invalid={
                                        !!(handleForm.touched.type && handleForm.errors.type)
                                    }
                                >
                                    Vídeo
                                </Label>
                                {handleForm.touched.type && handleForm.errors.type ? (
                                    <FormFeedback
                                        type="invalid"
                                        style={{display: 'block'}}>{handleForm.errors.type}</FormFeedback>
                                ) : null}
                            </div>
                            <div className="col">
                                <Input
                                    name="type"
                                    type="radio"
                                    id="type_audio"
                                    value="audio"
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    checked={handleForm.values.type === 'audio'}
                                    disabled={!!placement}
                                    invalid={
                                        !!(handleForm.touched.type && handleForm.errors.type)
                                    }
                                />
                                {' '}
                                <Label
                                    htmlFor="type_audio"
                                    className="form-label"
                                >
                                    Audio
                                </Label>
                                {handleForm.touched.type && handleForm.errors.type ? (
                                    <FormFeedback
                                        type="invalid"
                                        style={{display: 'block'}}>{handleForm.errors.type}</FormFeedback>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    <h3>Configurações</h3>
                    <div className="row">
                        <div className="col">
                            <div className="mb-3">
                                <Label
                                    htmlFor="maxRichMediaSizeInKb"
                                    className="form-label"
                                >
                                    Size máximo de Rich Media (Kb)
                                </Label>
                                <Input className="form-control"
                                       type="text"
                                       id="maxRichMediaSizeInKb"
                                       name="maxRichMediaSizeInKb"
                                       placeholder="150"
                                       onChange={handleForm.handleChange}
                                       onBlur={handleForm.handleBlur}
                                       value={handleForm.values.maxRichMediaSizeInKb}
                                       invalid={
                                           !!(handleForm.touched.maxRichMediaSizeInKb && handleForm.errors.maxRichMediaSizeInKb)
                                       }
                                />
                                {handleForm.touched.maxRichMediaSizeInKb && handleForm.errors.maxRichMediaSizeInKb ? (
                                    <FormFeedback
                                        type="invalid">{handleForm.errors.maxRichMediaSizeInKb}</FormFeedback>
                                ) : null}
                            </div>
                        </div>
                        <div className="col">
                            <div className="mb-3">
                                <Label
                                    htmlFor="maxImageSizeInKb"
                                    className="form-label"
                                >
                                    Size máximo da imagem (Kb)
                                </Label>
                                <Input
                                    className="form-control"
                                    type="text"
                                    id="maxImageSizeInKb"
                                    name="maxImageSizeInKb"
                                    placeholder="40"
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    value={handleForm.values.maxImageSizeInKb}
                                    invalid={
                                        !!(handleForm.touched.maxImageSizeInKb && handleForm.errors.maxImageSizeInKb)
                                    }
                                />
                                {handleForm.touched.maxImageSizeInKb && handleForm.errors.maxImageSizeInKb ? (
                                    <FormFeedback
                                        type="invalid">{handleForm.errors.maxImageSizeInKb}</FormFeedback>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <div className="mb-3">
                                <Label htmlFor="acceptsAudioVideo" className="form-label">
                                    Aceita Audio/Vídeo {handleForm.values.acceptsAudioVideo}
                                </Label>
                                <Input
                                    type="checkbox"
                                    id="acceptsAudioVideo"
                                    name="acceptsAudioVideo"
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    checked={handleForm.values.acceptsAudioVideo}
                                    invalid={!!(handleForm.touched.acceptsAudioVideo && handleForm.errors.acceptsAudioVideo)}
                                    value="true"
                                />
                                {handleForm.touched.acceptsAudioVideo && handleForm.errors.acceptsAudioVideo ? (
                                    <FormFeedback type="invalid">{handleForm.errors.acceptsAudioVideo}</FormFeedback>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <div className="mb-3">
                                <Label
                                    htmlFor="maxMediaSizeInKb"
                                    className="form-label"
                                >
                                    Size máximo do vídeo/audio (Kb)
                                </Label>
                                <Input
                                    className="form-control"
                                    type="text"
                                    id="maxMediaSizeInKb"
                                    name="maxMediaSizeInKb"
                                    placeholder="50000"
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    value={handleForm.values.maxMediaSizeInKb}
                                    invalid={
                                        !!(handleForm.touched.maxMediaSizeInKb && handleForm.errors.maxMediaSizeInKb)
                                    }
                                />
                                {handleForm.touched.maxMediaSizeInKb && handleForm.errors.maxMediaSizeInKb ? (
                                    <FormFeedback
                                        type="invalid">{handleForm.errors.maxMediaSizeInKb}</FormFeedback>
                                ) : null}
                            </div>
                        </div>
                        <div className="col">
                            <div className="mb-3">
                                <Label
                                    htmlFor="maxMediaDurationInSeconds"
                                    className="form-label"
                                >
                                    Tempo máximo de vídeo/audio (segundos)
                                </Label>
                                <Input
                                    className="form-control"
                                    type="text"
                                    id="maxMediaDurationInSeconds"
                                    name="maxMediaDurationInSeconds"
                                    placeholder="30"
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    value={handleForm.values.maxMediaDurationInSeconds}
                                    invalid={
                                        !!(handleForm.touched.maxMediaDurationInSeconds && handleForm.errors.maxMediaDurationInSeconds)
                                    }
                                />
                                {handleForm.touched.maxMediaDurationInSeconds && handleForm.errors.maxMediaDurationInSeconds ? (
                                    <FormFeedback
                                        type="invalid">{handleForm.errors.maxMediaDurationInSeconds}</FormFeedback>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    <div className="mb-3">
                        <Label
                            htmlFor="refreshInSeconds"
                            className="form-label"
                        >
                            Loop / Refresh (segundos)
                        </Label>
                        <Input
                            className="form-control"
                            type="text"
                            id="refreshInSeconds"
                            name="refreshInSeconds"
                            onChange={handleForm.handleChange}
                            onBlur={handleForm.handleBlur}
                            value={handleForm.values.refreshInSeconds}
                            invalid={
                                !!(handleForm.touched.refreshInSeconds && handleForm.errors.refreshInSeconds)
                            }
                        />
                        {handleForm.touched.refreshInSeconds && handleForm.errors.refreshInSeconds ? (
                            <FormFeedback
                                type="invalid">{handleForm.errors.refreshInSeconds}</FormFeedback>
                        ) : null}
                    </div>
                    <div className="mb-3">
                        <Label
                            htmlFor="notes"
                            className="form-label"
                        >
                            Observações
                        </Label>
                        <Input
                            className="form-control"
                            type="textarea"
                            id="notes"
                            name="notes"
                            onChange={handleForm.handleChange}
                            onBlur={handleForm.handleBlur}
                            value={handleForm.values.notes}
                            invalid={
                                !!(handleForm.touched.notes && handleForm.errors.notes)
                            }
                        />
                        {handleForm.touched.notes && handleForm.errors.notes ? (
                            <FormFeedback
                                type="invalid">{handleForm.errors.refreshInSeconds}</FormFeedback>
                        ) : null}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button
                        type="submit"
                        className="btn btn-success btn-label right ms-auto nexttab nexttab"
                    >
                        <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
                        {loading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                        {placement ? 'Atualizar' : 'Cadastrar'}
                    </button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default AddEditPlacementModal;